import { useState } from 'react'
import { ChevronDown, ChevronUp, MoreHorizontal, Plus } from 'lucide-react'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown'
import { Input } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import type { Segment } from '@/types'

/** What the rail counts: anything with a segment. Programs passes WB programs (PC-33). */
interface Segmented {
  segmentId: string | null
}

/** Sentinel for the fixed "Unassigned" row. Not a segment id. */
export const UNASSIGNED = '__unassigned__'

/**
 * Segments as a *filter*, not a competing CRUD surface — that is the redesign
 * (docs/03-page-specs.md §5.1). All and Unassigned are fixed rows; Unassigned
 * earns a permanent slot because it is the actionable bucket.
 *
 * Programs-local on purpose. There is exactly one of these, and one is not a
 * pattern — do not generalise it into a `<FilterRail>`.
 *
 * No drag-and-drop: reorder is ▲▼, reassignment is the table's dropdown.
 * Deferred deliberately to Phase 1.5, and the dropdown stays the primary path
 * even once drag lands.
 */
export function SegmentRail<T extends Segmented>({
  segments,
  defaults = [],
  programs,
  groupBy,
  value,
  onChange,
  onCreate,
  onRename,
  onMove,
  onEditDetails,
  onRequestDelete,
}: {
  segments: Segment[]
  /** Default segment names (PC-20), offered as quick adds while adding. */
  defaults?: string[]
  programs: T[]
  /** Count distinct keys rather than rows. */
  groupBy?: (p: T) => string
  value: string
  onChange: (next: string) => void
  onCreate: (name: string) => void
  onRename: (id: string, name: string) => void
  onMove: (id: string, direction: -1 | 1) => void
  /** Opens the segment's details (PC-24). */
  onEditDetails: (segment: Segment) => void
  onRequestDelete: (segment: Segment) => void
}) {
  const [renaming, setRenaming] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')

  const count = (rows: T[]) => (groupBy ? new Set(rows.map(groupBy)).size : rows.length)
  const countIn = (segmentId: string | null) => count(programs.filter((p) => p.segmentId === segmentId))

  // The defaults the client has not used yet, narrowed by what is typed.
  const taken = new Set(segments.map((s) => s.name.trim().toLowerCase()))
  const query = draft.trim().toLowerCase()
  const suggestions = defaults.filter((d) => !taken.has(d.toLowerCase()) && d.toLowerCase().includes(query))

  const create = (name: string) => {
    onCreate(name)
    setDraft('')
    setAdding(false)
  }

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-rule px-3 py-2">
        <p className="field-label">Segments</p>
      </div>

      <ul className="p-1.5">
        <RailRow
          label="All programs"
          count={count(programs)}
          active={value === 'all'}
          onClick={() => onChange('all')}
        />
        <RailRow
          label="Unassigned"
          count={countIn(null)}
          active={value === UNASSIGNED}
          onClick={() => onChange(UNASSIGNED)}
        />
        <li aria-hidden className="my-1.5 border-t border-rule" />

        {segments.length === 0 && !adding && (
          <li className="px-2.5 py-2 text-[12px] text-ink-faint">No segments yet.</li>
        )}

        {segments.map((segment, i) => (
          <li key={segment.id} className="group relative">
            {renaming === segment.id ? (
              <Input
                autoFocus
                defaultValue={segment.name}
                aria-label="Segment name"
                className="h-7 text-[13px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onRename(segment.id, (e.target as HTMLInputElement).value)
                    setRenaming(null)
                  }
                  if (e.key === 'Escape') setRenaming(null)
                }}
                onBlur={() => setRenaming(null)}
              />
            ) : (
              <div
                className={cn(
                  'flex items-center gap-1 rounded-[4px] pl-2.5 pr-1',
                  value === segment.id ? 'bg-brand-blue-100' : 'hover:bg-surface-sunk',
                )}
              >
                <button
                  type="button"
                  onClick={() => onChange(segment.id)}
                  className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left"
                >
                  <span className="nav-label truncate">{segment.name}</span>
                  <span className="tabular ml-auto text-[12px] text-ink-faint">
                    {countIn(segment.id)}
                  </span>
                </button>

                <div className="flex opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                  <IconButton
                    label={`Move ${segment.name} up`}
                    disabled={i === 0}
                    className="h-6 w-5"
                    onClick={() => onMove(segment.id, -1)}
                  >
                    <ChevronUp size={12} aria-hidden />
                  </IconButton>
                  <IconButton
                    label={`Move ${segment.name} down`}
                    disabled={i === segments.length - 1}
                    className="h-6 w-5"
                    onClick={() => onMove(segment.id, 1)}
                  >
                    <ChevronDown size={12} aria-hidden />
                  </IconButton>
                  <Dropdown
                    align="right"
                    trigger={
                      <IconButton label={`Actions for ${segment.name}`} className="h-6 w-5">
                        <MoreHorizontal size={12} aria-hidden />
                      </IconButton>
                    }
                  >
                    {(close) => (
                      <>
                        <DropdownItem
                          onClick={() => {
                            close()
                            onEditDetails(segment)
                          }}
                        >
                          Edit details
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => {
                            close()
                            setRenaming(segment.id)
                          }}
                        >
                          Rename
                        </DropdownItem>
                        <DropdownItem
                          destructive
                          onClick={() => {
                            close()
                            onRequestDelete(segment)
                          }}
                        >
                          Delete
                        </DropdownItem>
                      </>
                    )}
                  </Dropdown>
                </div>
              </div>
            )}
          </li>
        ))}

        <li className="mt-1.5 px-1">
          {adding ? (
            <Input
              autoFocus
              value={draft}
              placeholder="Segment name"
              aria-label="New segment name"
              className="h-7 text-[13px]"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && draft.trim()) create(draft.trim())
                if (e.key === 'Escape') {
                  setDraft('')
                  setAdding(false)
                }
              }}
              onBlur={() => !draft && setAdding(false)}
            />
          ) : (
            <Button size="sm" variant="quiet" onClick={() => setAdding(true)}>
              <Plus size={12} aria-hidden />
              Add segment
            </Button>
          )}
        </li>

        {adding && suggestions.length > 0 && (
          <li className="px-1 pt-1.5">
            <p className="px-1.5 pb-1 text-[11px] text-ink-faint">Defaults</p>
            <ul aria-label="Default segments" className="flex flex-wrap gap-1">
              {suggestions.map((name) => (
                <li key={name}>
                  <button
                    type="button"
                    // mousedown, not click: the input's blur would close the list first.
                    onMouseDown={(e) => {
                      e.preventDefault()
                      create(name)
                    }}
                    className="rounded-[4px] border border-rule px-1.5 py-0.5 text-[12px] hover:bg-surface-sunk"
                  >
                    {name}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        )}
      </ul>
    </Card>
  )
}

function RailRow({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-2 rounded-[4px] px-2.5 py-1.5 text-left',
          active ? 'bg-brand-blue-100' : 'hover:bg-surface-sunk',
        )}
      >
        <span className="nav-label">{label}</span>
        <span className="tabular ml-auto text-[12px] text-ink-faint">{count}</span>
      </button>
    </li>
  )
}
